import gql from 'graphql-tag';

import { Mutation, Query } from 'core';
import { Subscription } from 'core/subscription';

import { FILE_FIELDS } from 'apps/file/gql';

const ORIGIN_BASIC_FRAGMENT = gql`
  fragment origin on Contribution {
    id
    title
    version
    space {
      id
      spaceType
      content {
        id
        slug
      }
    }
  }
`;

const ORIGIN_DETAIL_FRAGMENT = gql`
  ${ORIGIN_BASIC_FRAGMENT}
  fragment originDetail on Contribution {
    ...origin
    project {
      id
      name
      slug
      forkCount
      isStarred
      starCount
      collections {
        id
        slug
      }
      space {
        id
        spaceType
        content {
          id
          slug
        }
      }
      content {
        id
        followersCount
        isFollowed
      }
    }
  }
`;

const OPERATIONS_FRAGMENT = gql`
  fragment operations on Contribution {
    operations {
      id
      opType
      path
    }
  }
`;

const FILES_FRAGMENT = gql`
  ${ORIGIN_BASIC_FRAGMENT}
  fragment files on Contribution {
    files(basepath: $basepath) {
      id
      file {
        id
        path
        size
        permalink
        completed
        gitHash
      }
      origin {
        ...origin
      }
      isFolder
      dirname
      filename
      lastUpdated
      repoRevision
    }
  }
`;

const FILE_DETAIL_FRAGMENT = gql`
  ${ORIGIN_DETAIL_FRAGMENT}
  fragment contribFile on Contribution {
    contribFile(filepath: $filepath) {
      id
      contribution {
        creatorProfile {
          username
        }
      }
      file {
        id
        filename
        gitHash
        mimeType
        path
        size
        fileLastModified
        completed
        url
        permalink
        originalFile {
          id
        }
        private
        space {
          id
          parent {
            id
          }
        }
        conversionGltfjson(conversionSpec: $conversionSpec) {
          id
          status
          reason
          errorCode
          convertedFile {
            id
          }
        }
      }
      origin {
        ...originDetail
      }
      dirname
      filename
    }
  }
`;

const CONTRIBUTION_FIELDS = gql`
  fragment contributionFields on Contribution {
    id
    title
    description
    status
    version
    dateCreated
    lastUpdated
    zipArchiveUrl
    creatorProfile {
      id
      username
      imageFallbackChar
      avatar {
        id
        path
      }
    }
    space {
      id
      spaceType
      content {
        id
        slug
      }
    }
  }
`;

const CONTRIBUTION_CONNECTION_FIELDS = gql`
  fragment contributionConnectionFields on ContributionConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        id
        title
        description
        status
        version
        zipArchiveUrl
        creatorProfile {
          id
          username
          imageFallbackChar
          avatar {
            id
            path
            url
          }
        }
        dateCreated
        lastUpdated
        commenters {
          id
          username
          avatar {
            id
            path
          }
        }
        content {
          id
          whitelabel
        }
      }
    }
  }
`;

// $basepath: String
const CLONE_CONTRIBUTION = gql`
  query CloneContribution($space: String, $slug: String) {
    project(space: $space, slug: $slug) {
      id
      cloneTree
    }
  }
`;
export class CloneContributionQuery extends Query {
  static conf = {
    GQL: CLONE_CONTRIBUTION,
    resultPath: 'project.cloneTree',
    varNames: ['space', 'slug'], // 'version', 'basepath', when version is an empty string '' the query seems to fail
  };
}

const PULL_CONTRIBUTION = gql`
  query PullContribution($space: String, $slug: String, $head: String) {
    project(space: $space, slug: $slug) {
      pullTree(head: $head)
    }
  }
`;
export class PullContributionQuery extends Query {
  static conf = {
    GQL: PULL_CONTRIBUTION,
    resultPath: 'project.pullTree',
    varNames: ['space', 'slug', 'head'],
  };
}

const CONTRIBUTION = gql`
  ${CONTRIBUTION_FIELDS}
  ${OPERATIONS_FRAGMENT}
  query Contribution($projectId: ID, $version: String) {
    project(id: $projectId) {
      contribution(version: $version) {
        ...contributionFields
        ...operations
      }
      contributions {
        totalCount
      }
      contributors {
        totalCount
      }
    }
  }
`;

const CONFLICT_FIELDS = gql`
  ${FILE_FIELDS}
  fragment conflictFields on Conflict {
    id
    path
    localStatus
    local {
      ...fileFields
    }
    remoteStatus
    remote {
      ...fileFields
    }
    remoteContribution {
      id
      title
      version
      lastUpdated
      creatorProfile {
        id
        imageFallbackChar
        username
        avatar {
          id
          path
          url
        }
      }
    }
  }
`;

export class ContributionQuery extends Query {
  static conf = {
    GQL: CONTRIBUTION,
    resultPath: 'project',
  };
}

const CONTRIBUTION_FILE_QUERY = gql`
  ${ORIGIN_BASIC_FRAGMENT}
  query ContributionFile(
    $space: String!
    $slug: String!
    $dirname: String!
    $filename: String!
  ) {
    contributionFile(
      space: $space
      slug: $slug
      dirname: $dirname
      filename: $filename
    ) {
      result(
        space: $space
        slug: $slug
        dirname: $dirname
        filename: $filename
      ) {
        id
        file {
          id
          filename
          mimeType
          path
          size
          completed
          fileLastModified
          permalink
        }
        origin {
          ...origin
        }
        dirname
        filename
        isFolder
      }
    }
  }
`;

export class ContributionFileQuery extends Query {
  static conf = {
    GQL: CONTRIBUTION_FILE_QUERY,
    resultPath: 'contributionFile',
  };
}

const CONTRIBUTIONS = gql`
  ${CONTRIBUTION_CONNECTION_FIELDS}
  query Contributions(
    $projectId: ID
    $batchSize: Int
    $cursor: String
    $sortBy: String
    $path: String
  ) {
    project(id: $projectId) {
      id
      contributions(
        first: $batchSize
        after: $cursor
        sortBy: $sortBy
        path: $path
      ) {
        ...contributionConnectionFields
      }
      isRestricted
    }
  }
`;

export class ContributionsQuery extends Query {
  static conf = {
    GQL: CONTRIBUTIONS,
    resultPath: 'project.contributions',
  };
}

const CONTRIBUTORS = gql`
  query Contributors(
    $projectId: ID
    $sortBy: String
    $batchSize: Int
    $cursor: String
  ) {
    project(id: $projectId) {
      id
      contributors(first: $batchSize, after: $cursor, sortBy: $sortBy) {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
        }
        totalCount
        edges {
          cursor
          node {
            id
            username
            fullName
            imageFallbackChar
            avatar {
              id
              path
              url
            }
            contributions(projectId: $projectId) {
              totalCount
            }
          }
        }
      }
    }
  }
`;

export class ContributorsQuery extends Query {
  static conf = {
    GQL: CONTRIBUTORS,
    resultPath: 'project.contributors',
  };
}

const CONTRIBUTION_PAGE_QUERY = gql`
  ${ORIGIN_DETAIL_FRAGMENT}
  ${CONTRIBUTION_FIELDS}
  ${OPERATIONS_FRAGMENT}
  query Contribution($space: String, $slug: String, $version: String) {
    project(space: $space, slug: $slug) {
      id
      slug
      image {
        id
        path
        url
        completed
        private
      }
      space {
        id
        spaceType
        content {
          id
          slug
        }
      }
      inSpace {
        id
        parent {
          id
          spaceType
          content {
            id
            slug
          }
        }
      }
      contribution(version: $version) {
        ...contributionFields
        ...operations
        ...originDetail
      }
    }
  }
`;

export class ContributionPageQuery extends Query {
  static conf = {
    GQL: CONTRIBUTION_PAGE_QUERY,
    resultPath: 'project',
  };
}

export const AUTOCOMPLETE_REPO_FILES = gql`
  query RepoFiles($space: String, $slug: String, $basepath: String) {
    project(space: $space, slug: $slug) {
      id
      contribution {
        id
        files(basepath: $basepath) {
          id
          filename
          dirname
          isFolder
        }
      }
    }
  }
`;

export class AutocompleteRepositoryFilesQuery extends Query {
  static conf = {
    GQL: AUTOCOMPLETE_REPO_FILES,
    varNames: ['space', 'slug', 'basepath'],
    resultPath: 'project.contribution',
  };
}

const PENDING_OPERATIONS_FRAGMENT = gql`
  ${ORIGIN_BASIC_FRAGMENT}
  fragment pendingOperations on Project {
    pendingOperations {
      id
      opType
      path
      oldPath
      origin {
        ...origin
      }
      isFile
    }
  }
`;

const CONTRIBUTION_UPSTREAM_FRAGMENT = gql`
  ${FILES_FRAGMENT}
  ${CONTRIBUTION_FIELDS}
  fragment contributionUpstream on Project {
    contributionUpstream {
      ...contributionFields
      ...files
    }
  }
`;

const CONTRIBUTION_VERSION_FRAGMENT = gql`
  ${FILES_FRAGMENT}
  ${CONTRIBUTION_FIELDS}
  fragment contributionVersion on Project {
    contribution(version: $version) {
      ...contributionFields
      ...files
    }
  }
`;

const REPOSITORY_FRAGMENT = gql`
  ${CONTRIBUTION_UPSTREAM_FRAGMENT}
  ${CONTRIBUTION_VERSION_FRAGMENT}
  ${PENDING_OPERATIONS_FRAGMENT}
  fragment repository on Project {
    id
    contextId
    inSpace {
      id
      whichTypes
      flags
    }
    ...contributionVersion
    ...contributionUpstream
    ...pendingOperations
    contributionCount
    contributors {
      totalCount
    }
  }
`;

const REPOSITORY_QUERY = gql`
  ${REPOSITORY_FRAGMENT}
  query Repository(
    $space: String
    $slug: String
    $basepath: String
    $version: String
  ) {
    project(space: $space, slug: $slug) {
      ...repository
      name
      context {
        id
      }
      isRestricted
      canDownload
    }
  }
`;

export const ZIP_STATUS_SUBSCRIPTION = gql`
  subscription ZipStatus($input: SubscribeZipStatusInput!) {
    zipStatus(input: $input)
  }
`;

export class RepoQuery extends Query {
  static conf = {
    GQL: REPOSITORY_QUERY,
    varNames: ['space', 'slug', 'basepath', 'version'],
    resultPath: 'project',
  };
}

const PENDING_OPERATIONS_QUERY = gql`
  ${PENDING_OPERATIONS_FRAGMENT}
  query PendingOperations($space: String, $slug: String) {
    project(space: $space, slug: $slug) {
      id
      ...pendingOperations
    }
  }
`;
export class PendingOperationsQuery extends Query {
  static conf = {
    GQL: PENDING_OPERATIONS_QUERY,
    varNames: ['space', 'slug'],
    resultPath: 'project',
  };
}

export const POLL_ZIP_QUERY = gql`
  query RepositoryZip($space: String, $slug: String) {
    project(space: $space, slug: $slug) {
      id
      contributionUpstream {
        id
        zipArchiveUrl
      }
    }
  }
`;

export class PollZipQuery extends Query {
  static conf = {
    GQL: POLL_ZIP_QUERY,
    varNames: ['space', 'slug'],
    resultPath: 'project.contributionUpstream',
  };
}

export class ZipStatusSubscription extends Subscription {
  static conf = {
    GQL: ZIP_STATUS_SUBSCRIPTION,
    resultPath: 'zipStatus',
  };
}

const CANCEL_CONTRIBUTION_MUTATION = gql`
  ${REPOSITORY_FRAGMENT}
  mutation ResetContribution(
    $resetData: ResetContributionInput
    $basepath: String
    $version: String
  ) {
    resetContribution(resetData: $resetData) {
      project {
        ...repository
      }
    }
  }
`;

export class CancelContributionMutation extends Mutation {
  static conf = {
    GQL: CANCEL_CONTRIBUTION_MUTATION,
    inputName: ['resetData', 'basepath', 'version'],
    resultPath: 'resetContribution.project',
  };
}

const OPERATION_MUTATION = gql`
  mutation Operation($operationData: OperationInput) {
    operation(operationData: $operationData) {
      opType
      path
      ignored
    }
  }
`;

export class OperationMutation extends Mutation {
  static conf = {
    GQL: OPERATION_MUTATION,
    inputName: 'operationData',
    resultPath: 'operation',
  };
}

const CONFLICT_RESOLUTION_FRAGMENT = gql`
  ${CONFLICT_FIELDS}
  fragment conflictResolution on Project {
    conflictsParent
    conflicts {
      ...conflictFields
    }
  }
`;

const COMMIT_CONTRIBUTION_MUTATION = gql`
  ${CONFLICT_RESOLUTION_FRAGMENT}
  ${REPOSITORY_FRAGMENT}
  mutation CommitContribution(
    $commitData: CommitInput
    $basepath: String
    $version: String
  ) {
    commit(commitData: $commitData) {
      project {
        ...repository
        ...conflictResolution
      }
    }
  }
`;

const CREATE_PROJECT_ARCHIVE_MUTATION = gql`
  mutation ProjectCreateArchive($input: ProjectCreateArchiveInput!) {
    projectCreateArchive(input: $input) {
      result
    }
  }
`;

export class CreateProjectArchiveMutation extends Mutation {
  static conf = {
    GQL: CREATE_PROJECT_ARCHIVE_MUTATION,
    inputName: 'input',
    resultPath: 'projectCreateArchive',
  };
}
export class CommitContributionMutation extends Mutation {
  static conf = {
    GQL: COMMIT_CONTRIBUTION_MUTATION,
    inputName: ['commitData', 'basepath', 'version'],
    resultPath: 'commit.project',
  };
}

const CHECKIN_CONTRIBUTION_MUTATION = gql`
  mutation CheckinContribution($checkinData: CheckinInput) {
    checkin(checkinData: $checkinData) {
      hash
    }
  }
`;

export class CheckinContributionMutation extends Mutation {
  static conf = {
    GQL: CHECKIN_CONTRIBUTION_MUTATION,
    inputName: ['checkinData'],
    resultPath: 'checkin',
  };
}

const LOCK_PATH_MUTATION = gql`
  mutation LockPath($lockInput: LockPathInput!) {
    lockPath(lockInput: $lockInput) {
      result {
        path
        locked
        lockedBy {
          username
        }
      }
    }
  }
`;
export class LockPathMutation extends Mutation {
  static conf = {
    GQL: LOCK_PATH_MUTATION,
    inputName: ['lockInput'],
    resultPath: 'lockPath.result',
  };
}

const LOCKED_FILES_QUERY = gql`
  query LockedFiles($space: String, $slug: String, $path: String) {
    project(space: $space, slug: $slug) {
      id
      lockedFiles(path: $path) {
        path
        # locked
        lockedBy {
          id
          username
        }
      }
    }
  }
`;
export class LockedFilesQuery extends Query {
  static conf = {
    GQL: LOCKED_FILES_QUERY,
    resultPath: 'project.lockedFiles',
    varNames: ['space', 'slug', 'path'],
  };
}

const PENDING_OPERATIONS_WITH_FILE_FRAGMENT = gql`
  ${FILE_FIELDS}
  ${ORIGIN_BASIC_FRAGMENT}
  fragment pendingOperationsWithFileFragment on Project {
    pendingOperations {
      id
      opType
      path
      oldPath
      origin {
        ...origin
      }
      isFile
      file {
        ...fileFields
      }
    }
  }
`;

const CONTRIBUTION_FILE_DETAILS_QUERY = gql`
  ${FILE_DETAIL_FRAGMENT}
  ${PENDING_OPERATIONS_WITH_FILE_FRAGMENT}
  query ContributionFileDetails(
    $space: String!
    $slug: String!
    $filepath: String!
    $conversionSpec: String
  ) {
    project(space: $space, slug: $slug) {
      id
      name
      private
      isRestricted
      canDownload
      context {
        id
        parentObj {
          id
          canUpdate
        }
      }
      contributionUpstream {
        id
        ...contribFile
      }
      ...pendingOperationsWithFileFragment
    }
  }
`;

export class ContributionFileDetailsQuery extends Query {
  static conf = {
    GQL: CONTRIBUTION_FILE_DETAILS_QUERY,
    resultPath: 'project',
  };
}

const CONTRIBUTION_FILE_VERSION_DETAILS_QUERY = gql`
  ${FILE_DETAIL_FRAGMENT}
  query ContributionFileVersionDetails(
    $space: String!
    $slug: String!
    $filepath: String!
    $version: String
    $conversionSpec: String
  ) {
    project(space: $space, slug: $slug) {
      id
      name
      private
      isRestricted
      context {
        id
        parentObj {
          id
          canUpdate
        }
      }
      contribution(version: $version) {
        id
        ...contribFile
      }
    }
  }
`;

const REPO_CONFLICTS = gql`
  ${CONFLICT_RESOLUTION_FRAGMENT}
  query Conflicts($space: String, $slug: String) {
    project(space: $space, slug: $slug) {
      id
      ...conflictResolution
    }
  }
`;

export class ContributionFileVersionDetailsQuery extends Query {
  static conf = {
    GQL: CONTRIBUTION_FILE_VERSION_DETAILS_QUERY,
    resultPath: 'project',
  };
}

export class ConflictsQuery extends Query {
  static conf = {
    GQL: REPO_CONFLICTS,
    resultPath: 'project',
  };
}

const BULK_DOWNLOAD = gql`
  query BulkDownload($space: String, $slug: String, $hashes: [String]) {
    project(space: $space, slug: $slug) {
      bulkDownload(hashes: $hashes)
    }
  }
`;

export class BulkDownloadQuery extends Query {
  static conf = {
    GQL: BULK_DOWNLOAD,
    resultPath: 'project.bulkDownload',
    varNames: ['space', 'slug', 'hashes'],
  };
}
